import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Package } from 'lucide-react';
import { Button, Separator } from '@scio3/ui';
import { formatCurrencyAmount } from '../../utils/plan-helpers';
import { formatDiscountLabel, sumDiscountAmounts } from '../../utils/format-coupon';
import { aggregateInvoiceLines } from '../../utils/aggregate-invoice-lines';
import { safeFormatDate } from '@/lib/safe-format';

/**
 * Finish action types matching scio1's isFinishAction:
 * - trial: new subscription from trial
 * - increase: added screens (paid)
 * - decrease: reduced screens (yearly, with refund)
 * - decreaseNotRefund: reduced screens (monthly, no refund)
 */
type FinishAction = 'trial' | 'increase' | 'decrease' | 'decreaseNotRefund';

type ChargeResult = {
  finishAction: FinishAction;
  email?: string;
  planName?: string;
  billingCycle?: string;
  billingCycleText?: string;
  pricePerScreen?: number;
  screens?: number;
  subtotal?: number;
  credit?: number;
  /**
   * Top-level tax line items from getUpcomingSubscription (matches scio1 `res.taxs`).
   * Each entry: `{ name: 'VAT - Vietnam(10%)', tax: <cents> }`.
   */
  taxs?: Array<{ name?: string; tax?: number }>;
  total?: number;
  totalPaidToday?: number;
  cardBrand?: string;
  cardLast4?: string;
  /** Addon line items for the receipt (e.g. Video Wall) */
  addonItems?: Array<{
    key: string;
    name: string;
    pricePerUnit: number;
    quantity: number;
    total: number;
    /** Tier within a multi-tier addon (e.g. 'standard' | 'pro').
     *  When set, name renders with " — Pro"/" — Standard" suffix — scio1 parity (SP46). */
    planFeature?: string;
  }>;
  /** Upcoming invoice data for increase/decreaseNotRefund flows */
  upcoming?: {
    created?: number;
    total?: number;
    subtotal?: number;
    amount_due?: number;
    ending_balance?: number;
    taxs?: Array<{ name?: string; tax?: number }>;
    /**
     * Stripe Upcoming Invoice line items — one row per plan/addon. Backend returns
     * the full Stripe Invoice object which includes `lines.data[]` with `description`
     * (e.g. "17 × Engage Plan (at $30.00 / month)"), `quantity`, and `amount` (cents).
     */
    lines?: {
      data?: Array<{
        description?: string | null;
        quantity?: number | null;
        amount?: number | null;
        /** #1213 — aggregation key, see `aggregateInvoiceLines`. */
        subscription_item?: string | null;
        price?: { product?: string | null } | null;
      }>;
    };
    /** Stripe upcoming-invoice discount metadata — drives the optional
     *  "Discount {coupon} -$X" row (#1220). */
    discount?: {
      coupon?: { name?: string; percent_off?: number; amount_off?: number };
    };
    /** Stripe `total_discount_amounts` — array of `{ amount: cents }` entries,
     *  one per applied coupon. */
    total_discount_amounts?: Array<{ amount?: number }>;
  };
  /** Summary text for increase: "Your subscription is updated to X screens total $Y per Z" */
  summaryText?: string;
  /** Free device coupon code returned after yearly_freedevice subscription */
  orderDeviceCoupon?: string;
  /** Number of free devices the coupon covers */
  orderDeviceCouponNumber?: number;
};

type Props = {
  chargeResult: ChargeResult | null;
  currency: string;
  currencySymbol: string;
  currentCard?: { brand: string; last4: string } | null;
  onClose: () => void;
};

export function WizardStepFinish({
  chargeResult,
  currency,
  currencySymbol: _currencySymbol,
  currentCard,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const finishAction = chargeResult?.finishAction ?? 'trial';
  const isUpcomingCharge = finishAction === 'increase' || finishAction === 'decreaseNotRefund';
  const hasOrderDeviceCoupon = !!(
    chargeResult?.orderDeviceCoupon && chargeResult?.orderDeviceCouponNumber
  );
  const cardBrand = (currentCard?.brand ?? chargeResult?.cardBrand ?? '').toUpperCase();
  const cardLast4 = currentCard?.last4 ?? chargeResult?.cardLast4;

  // Tax line items for the increase/decreaseNotRefund "Upcoming Charge" table.
  // Prefer NESTED `upcoming.taxs` (next-period total_tax_amounts via
  // retrieveUpcoming({ automatic_tax: enabled })) — that's what
  // UpcomingChargeDialog uses and it's the correct $X amount that pairs
  // with the table's subtotal/total. Top-level `chargeResult.taxs` sources
  // the proration-delta tax of the just-issued invoice (e.g. $1.01) which
  // doesn't reconcile with the displayed $60 subtotal. Fall back to the
  // top-level only when Stripe stripped the nested field (legacy).
  const upcomingTaxs =
    chargeResult?.upcoming?.taxs && chargeResult.upcoming.taxs.length > 0
      ? chargeResult.upcoming.taxs
      : (chargeResult?.taxs ?? []);

  // Discount row (#1220) — Thank-You receipt for Update Subscription. Only
  // render when the upcoming invoice carries an invoice-level coupon discount.
  const upcomingDiscountAmount = sumDiscountAmounts(chargeResult?.upcoming?.total_discount_amounts);
  const upcomingDiscountLabel = formatDiscountLabel(chargeResult?.upcoming?.discount?.coupon);

  return (
    <div className="py-6" data-testid="wizard-step-finish">
      {/* Thank you header */}
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold" data-testid="wizard-step-finish-heading">
          {t('Thank you')}!
        </h1>
        {isUpcomingCharge ? (
          /* Increase/decreaseNotRefund: summary text */
          chargeResult?.summaryText && (
            <p className="text-sm text-muted-foreground mt-2">{chargeResult.summaryText}</p>
          )
        ) : (
          /* Trial/decrease: "for your subscription" */
          <p className="text-lg text-primary font-medium">{t('for your subscription')}</p>
        )}
      </div>

      {isUpcomingCharge && chargeResult?.upcoming ? (
        /* === INCREASE / DECREASE_NOT_REFUND: "Upcoming Charge" table === */
        <div className="max-w-xl mx-auto rounded-lg border bg-card p-6">
          <div className="text-center mb-4">
            <h2 className="text-xl font-semibold">{t('Upcoming Charge')}</h2>
            {chargeResult.upcoming.created && (
              <p className="text-sm text-muted-foreground">
                {safeFormatDate(
                  new Date(chargeResult.upcoming.created * 1000).toISOString(),
                  'MM/dd/yyyy',
                )}
              </p>
            )}
          </div>

          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 font-semibold text-muted-foreground">
                  {t('DESCRIPTION')}
                </th>
                <th className="text-center py-2 font-semibold text-muted-foreground">{t('QTY')}</th>
                <th className="text-right py-2 font-semibold text-muted-foreground">
                  {t('AMOUNT')}
                </th>
              </tr>
            </thead>
            <tbody>
              {/* Itemized line items from Stripe Upcoming Invoice — one row per plan/addon.
                  Each `description` is pre-formatted by Stripe (e.g. "17 × Engage Plan
                  (at $30.00 / month)"). Aggregated via `aggregateInvoiceLines` (#1213) so
                  Stripe's per-period proration rows for the same `subscription_item`
                  collapse into a single row per addon. Falls back to a single bundled
                  row if `lines.data` is missing — defensive against unexpected
                  Stripe response shapes. */}
              {chargeResult.upcoming.lines?.data && chargeResult.upcoming.lines.data.length > 0 ? (
                aggregateInvoiceLines(chargeResult.upcoming.lines.data).map((line, i) => (
                  <tr key={i} className="border-b">
                    <td className="py-3">{line.description ?? ''}</td>
                    <td className="py-3 text-center">{line.quantity ?? ''}</td>
                    <td className="py-3 text-right">
                      {formatCurrencyAmount(getAmount(line.amount ?? 0), currency)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr className="border-b">
                  <td className="py-3">
                    {t('Pro-rated charge for increasing/decreasing of screens')}
                  </td>
                  <td className="py-3 text-center">{chargeResult.screens}</td>
                  <td className="py-3 text-right">
                    {formatCurrencyAmount(getAmount(chargeResult.upcoming.total), currency)}
                  </td>
                </tr>
              )}
              {/* Subtotal — always shown to match scio1 layout */}
              <tr className="border-b">
                <td className="py-2" />
                <td className="py-2 text-center">{t('Subtotal')}</td>
                <td className="py-2 text-right">
                  {formatCurrencyAmount(getAmount(chargeResult.upcoming.subtotal), currency)}
                </td>
              </tr>
              {/* Discount row (#1220) — between Subtotal and tax. */}
              {upcomingDiscountAmount > 0 && (
                <tr className="border-b" data-testid="upcoming-charge-discount-row">
                  <td className="py-2" />
                  <td className="py-2 text-center">
                    {t('Discount')}
                    {upcomingDiscountLabel ? ` - ${upcomingDiscountLabel}` : ''}
                  </td>
                  <td className="py-2 text-right">
                    -{formatCurrencyAmount(upcomingDiscountAmount, currency)}
                  </td>
                </tr>
              )}
              {/* Tax rows */}
              {upcomingTaxs.map((tax, i) => (
                <tr key={i} className="border-b">
                  <td className="py-2" />
                  <td className="py-2 text-center">{tax.name}</td>
                  <td className="py-2 text-right">
                    {formatCurrencyAmount(getAmount(tax.tax), currency)}
                  </td>
                </tr>
              ))}
              {/* Total */}
              <tr className="border-b">
                <td className="py-2" />
                <td className="py-2 text-center font-semibold">{t('Total')}</td>
                <td className="py-2 text-right font-semibold">
                  {formatCurrencyAmount(getAmount(chargeResult.upcoming.total), currency)}
                </td>
              </tr>
              {/* Applied balance — shown whenever Stripe returns the field, to match scio1 layout */}
              {chargeResult.upcoming.ending_balance != null && (
                <tr className="border-b">
                  <td className="py-2" />
                  <td className="py-2 text-center">{t('Applied balance')}</td>
                  <td className="py-2 text-right">
                    {formatCurrencyAmount(
                      getAmount(chargeResult.upcoming.ending_balance),
                      currency,
                    )}
                  </td>
                </tr>
              )}
              {/* Amount due */}
              <tr>
                <td className="py-2" />
                <td className="py-2 text-center font-semibold">{t('Amount due')}</td>
                <td className="py-2 text-right font-semibold">
                  {formatCurrencyAmount(getAmount(chargeResult.upcoming.amount_due), currency)}
                </td>
              </tr>
            </tbody>
          </table>

          <div className="text-center mt-6 space-y-3">
            {hasOrderDeviceCoupon && (
              <Button
                className="cursor-pointer px-8 gap-2"
                onClick={() => {
                  onClose();
                  navigate(
                    `/app/s/order-device?coupon=${chargeResult.orderDeviceCoupon}&couponNumber=${chargeResult.orderDeviceCouponNumber}&source=subscription`,
                  );
                }}
              >
                <Package className="h-4 w-4" />
                {t('Order Your Free Device')}
              </Button>
            )}
            <div>
              <Button
                variant={hasOrderDeviceCoupon ? 'ghost' : 'default'}
                onClick={onClose}
                className="cursor-pointer px-8"
                data-testid="wizard-finish-close-btn"
              >
                {hasOrderDeviceCoupon ? t('Continue Later') : t('Continue')}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        /* === TRIAL / DECREASE: Receipt card === */
        <div className="max-w-xl mx-auto rounded-lg border bg-card p-6 space-y-4">
          {/* Email */}
          {chargeResult?.email && (
            <div className="text-center text-sm">
              <p className="text-muted-foreground">{t('Copy of receipt has been emailed to:')}</p>
              <a href={`mailto:${chargeResult.email}`} className="text-primary">
                {chargeResult.email}
              </a>
            </div>
          )}

          <Separator />

          {/* Plan + screens */}
          <div className="flex justify-between text-sm">
            <div>
              <p className="font-semibold">
                {chargeResult?.planName} - {chargeResult?.billingCycle}
              </p>
              <p className="text-muted-foreground">
                {chargeResult?.screens} {t('screens')}
              </p>
            </div>
            <span className="font-medium">
              {formatCurrencyAmount(
                (chargeResult?.pricePerScreen ?? 0) * (chargeResult?.screens ?? 0),
                currency,
              )}
            </span>
          </div>

          {/* Addon line items (e.g. Video Wall, OptiSound Music) — shows breakdown between plan and subtotal.
              Tiered addons render as "{name} Add-on — {Tier}" (SP46, matches WizardStepConfirmation). */}
          {chargeResult?.addonItems?.map((addon) => {
            const tierLabel =
              addon.planFeature === 'pro'
                ? t('Pro')
                : addon.planFeature === 'standard'
                  ? t('Standard')
                  : null;
            const displayName = tierLabel
              ? `${addon.name} ${t('Add-on')} — ${tierLabel}`
              : addon.name;
            return (
              <div key={addon.key} className="flex justify-between text-sm">
                <div>
                  <p className="font-semibold">{displayName}</p>
                  <p className="text-muted-foreground">
                    {addon.quantity} {addon.quantity === 1 ? t('screen') : t('screens')}
                  </p>
                </div>
                <span className="font-medium">{formatCurrencyAmount(addon.total, currency)}</span>
              </div>
            );
          })}

          <Separator />

          {/* Subtotal */}
          <div className="flex justify-between text-sm">
            <span className="font-semibold">{t('Subtotal')}</span>
            <span>{formatCurrencyAmount(chargeResult?.subtotal ?? 0, currency)}</span>
          </div>

          {/* Credit */}
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{t('Credit for previous purchase')}</span>
            <span className="text-primary">
              {formatCurrencyAmount(chargeResult?.credit ?? 0, currency)}
            </span>
          </div>

          <Separator />

          {/* Tax line items (e.g. "VAT - Vietnam(10%)") — matches scio1
              subscriptionPlanModal.html:891 placement between Credit and Est Next Bill Total */}
          {chargeResult?.taxs?.map((tax, i) => (
            <div key={i} className="flex justify-between text-sm">
              <span className="font-semibold">{tax.name}</span>
              <span>{formatCurrencyAmount(getAmount(tax.tax), currency)}</span>
            </div>
          ))}

          {/* Est Next Bill Total + Total paid today */}
          <div className="flex justify-between text-sm">
            <span className="font-semibold">{t('Est Next Bill Total')}</span>
            <span>{formatCurrencyAmount(chargeResult?.total ?? 0, currency)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="font-semibold">{t('Total paid today for changes')}</span>
            <span>{formatCurrencyAmount(chargeResult?.totalPaidToday ?? 0, currency)}</span>
          </div>

          {/* Payment method */}
          {cardLast4 && (
            <p className="text-center text-sm text-muted-foreground">
              {t('Payment method')} {cardBrand} - {cardLast4}
            </p>
          )}

          <Separator />

          {/* Action buttons */}
          <div className="text-center space-y-3">
            {hasOrderDeviceCoupon && (
              <Button
                className="cursor-pointer px-8 gap-2"
                onClick={() => {
                  onClose();
                  navigate(
                    `/app/s/order-device?coupon=${chargeResult.orderDeviceCoupon}&couponNumber=${chargeResult.orderDeviceCouponNumber}&source=subscription`,
                  );
                }}
              >
                <Package className="h-4 w-4" />
                {t('Order Your Free Device')}
              </Button>
            )}
            <div>
              <Button
                variant={hasOrderDeviceCoupon ? 'ghost' : 'default'}
                onClick={onClose}
                className="cursor-pointer px-8"
                data-testid="wizard-finish-close-btn"
              >
                {hasOrderDeviceCoupon ? t('Continue Later') : t('Continue')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Convert Stripe amounts (cents) to dollars, handling negative values */
function getAmount(value?: number): number {
  if (value == null) return 0;
  return Math.abs(value) / 100;
}
